import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '@/shared/ui/kid-chrome.css';

/**
 * The shared KID BACK BUTTON — one calm `‹` chevron that gets a child out of any drilled-in kid surface
 * (missions, a habit record, the shop, the wallet…). The cozy-pastel counterpart to the guardian
 * {@link BackButton}; promotes the one-off `.detail-back` chevron the habit record hand-rolled into a
 * single reusable affordance so every kid sub-page carries the SAME way out. Render inside a `.kid`-scoped
 * page. Defaults to a history "back"; pass `to` for a page reachable by deep link (no in-app history) so
 * the chevron still lands somewhere sensible.
 */
export function KidBackButton({
  to,
  label,
  className,
}: {
  /** Explicit destination for a deep-linkable page. Omit to step back in history (`navigate(-1)`). */
  to?: string;
  /** Overrides the default "Back" accessible name. */
  label?: string;
  className?: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      className={className != null ? `kid-back ${className}` : 'kid-back'}
      aria-label={label ?? t('nav.back')}
      onClick={() => (to != null ? void navigate(to) : void navigate(-1))}
    >
      <span aria-hidden="true">‹</span>
    </button>
  );
}
